import { useState } from "react";
import { Empty, Spin } from "antd";
import { Copy } from "lucide-react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import CustomPagination from "../../../../components/custom-component/CustomPagination";
import {
    DataTable,
    FilteredEmpty,
    MobileFilterBar,
    RowActionsMenu,
    useTableFilters,
} from "../../../../components/data-table";
import CouponCodeMobileCard from "./CouponCodeMobileCard";
import { useToast } from "../../../../contexts/ToastContext";

const COUPON_ACTION_ITEMS = [
    { label: "View", key: "view" },
    { label: "Update", key: "update" },
    { label: "Delete", key: "delete", danger: true },
];

const formatDiscountLabel = (record) => {
    if (record.discountType === "PERCENTAGE") {
        return `${record.discountValue || 0}%${record.maxDiscountValue ? ` (max $${record.maxDiscountValue})` : ""}`;
    }
    return `$${record.discountValue || 0}`;
};

const CouponCodeTable = ({
    couponCodes,
    loading,
    currentPage,
    pageSize,
    totalItems,
    setCurrentPage,
    setPageSize,
    onView,
    onUpdate,
    onDelete,
    onCodeSearch,
    searchCode = "",
}) => {
    const { t } = useTranslation("settings");
    const toast = useToast();
    const [copiedKey, setCopiedKey] = useState(null);
    const table = useTableFilters({
        value: searchCode ? { code: searchCode } : {},
        onChange: (next) => onCodeSearch(next.code ?? ""),
    });

    const handleCopyCouponCode = (code, key) => {
        if (!code || code === "N/A") return;

        navigator.clipboard
            .writeText(code)
            .then(() => {
                setCopiedKey(key);
                toast.success({ message: t("couponCode.copiedToClipboard") });

                setTimeout(() => {
                    setCopiedKey(null);
                }, 2000);
            })
            .catch(() => {
                toast.error({ message: t("couponCode.copyFailed") });
            });
    };

    const handleActionClick = ({ key }, record) => {
        if (key === "view") {
            onView(record);
        } else if (key === "update") {
            onUpdate(record);
        } else if (key === "delete") {
            onDelete(record);
        }
    };

    const emptyDescription = "No coupon codes available. Create coupon codes to start offering discounts.";

    // Table columns definition
    const columns = [
        {
            title: "Code",
            dataIndex: "code",
            key: "code",
            width: 150,
            ...table.getColumnSearchProps("code", { placeholder: "Search by code..." }),
            render: (code, record, index) => {
                const rowKey = record._id || record.key || index;
                return (
                    <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-sm font-semibold text-[#5D5DF6]">{code || "N/A"}</span>
                        <button
                            type="button"
                            onClick={() => handleCopyCouponCode(code, rowKey)}
                            className={`rounded-md p-1 ${
                                !code || code === "N/A"
                                    ? "cursor-not-allowed text-gray-300"
                                    : "text-gray-500 hover:text-blue-600 active:text-blue-700"
                            }`}
                            disabled={!code || code === "N/A"}>
                            <Copy size={12} className={copiedKey === rowKey ? "text-green-500" : ""} />
                        </button>
                    </div>
                );
            },
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            width: 220,
            ellipsis: true,
            render: (text) => (
                <span className="text-sm">{text || "N/A"}</span>
            ),
        },
        {
            title: "Discount",
            key: "discount",
            width: 170,
            render: (_, record) => (
                <span className="whitespace-nowrap text-sm">{formatDiscountLabel(record)}</span>
            ),
        },
        {
            title: "Expiry Date",
            dataIndex: "expiryDate",
            key: "expiryDate",
            width: 130,
            render: (date) => (
                <span className="whitespace-nowrap text-sm">{date ? new Date(date).toLocaleDateString() : "N/A"}</span>
            ),
        },
        {
            title: "Usage",
            key: "usage",
            width: 100,
            render: (_, record) => (
                <span className="whitespace-nowrap text-sm">{`${record.usedCount || 0} / ${record.maxUses || "∞"}`}</span>
            ),
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            width: 110,
            render: (isActive) => (
                <span
                    className={`whitespace-nowrap rounded-full px-2 py-1 text-sm font-medium ${
                        isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                    }`}>
                    {isActive ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            title: "Created Date",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 130,
            render: (date) => (
                <span className="whitespace-nowrap text-sm">{date ? new Date(date).toLocaleDateString() : "N/A"}</span>
            ),
        },
        {
            title: <span className="sr-only">Actions</span>,
            key: "actions",
            width: 64,
            align: "center",
            fixed: "right",
            render: (_, record) => (
                <RowActionsMenu
                    items={COUPON_ACTION_ITEMS}
                    onClick={(info) => handleActionClick(info, record)}
                    label="Actions"
                />
            ),
        },
    ];

    const statusBadge = (isActive) => (
        <span
            className={`inline-flex rounded-full px-2 py-1 text-sm font-medium ${
                isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
            }`}>
            {isActive ? "Active" : "Inactive"}
        </span>
    );

    const mobileCouponList = (
            <div className="min-w-0 space-y-3">
                <MobileFilterBar
                    filters={table}
                    search={{ key: "code", placeholder: "Search by code..." }}
                    filterLabels={{ code: "Code" }}
                />
                {loading ? (
                    <div className="flex justify-center py-12">
                        <Spin />
                    </div>
                ) : !couponCodes?.length ? (
                    <FilteredEmpty filters={table} description={emptyDescription} />
                ) : (
                    <div className="flex flex-col gap-3">
                        {couponCodes.map((coupon) => {
                            const rowKey = coupon._id || coupon.key;
                            const expiry = coupon.expiryDate
                                ? new Date(coupon.expiryDate).toLocaleDateString()
                                : "N/A";
                            const created = coupon.createdAt
                                ? new Date(coupon.createdAt).toLocaleDateString()
                                : "N/A";

                            return (
                                <CouponCodeMobileCard
                                    key={rowKey}
                                    coupon={coupon}
                                    copied={copiedKey === rowKey}
                                    onCopy={() => handleCopyCouponCode(coupon.code, rowKey)}
                                    menuItems={COUPON_ACTION_ITEMS}
                                    onMenuClick={handleActionClick}
                                    discountLabel={formatDiscountLabel(coupon)}
                                    rows={[
                                        { label: "Expiry Date", value: expiry },
                                        {
                                            label: "Usage",
                                            value: `${coupon.usedCount || 0} / ${coupon.maxUses || "∞"}`,
                                        },
                                        { label: "Status", value: statusBadge(coupon.isActive) },
                                        { label: "Created Date", value: created },
                                    ]}
                                />
                            );
                        })}
                    </div>
                )}
                <div className="flex min-w-0 w-full justify-end pt-2">
                    <CustomPagination
                        variant="spaced"
                        totalItem={totalItems}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSizeOptions={[5, 10, 20, 30, 50]}
                        showSizeChanger
                    />
                </div>
            </div>
    );

    const desktopCouponTable = (
        <>
            <DataTable
                filters={table}
                search={{ key: "code", placeholder: "Search by code..." }}
                filterLabels={{ code: "Code" }}
                columns={columns}
                dataSource={couponCodes}
                loading={loading}
                rowKey={(record) => record._id || record.key}
                scroll={{ x: 1110 }}
                emptyText={<Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                pagination={{
                    total: totalItems,
                    current: currentPage,
                    pageSize,
                    onChange: setCurrentPage,
                    onPageSizeChange: setPageSize,
                    pageSizeOptions: [5, 10, 20, 30, 50],
                }}
            />
        </>
    );

    return (
        <>
            <div className="flex min-w-0 flex-col gap-4 lg:hidden">{mobileCouponList}</div>
            <div className="hidden min-w-0 lg:block">{desktopCouponTable}</div>
        </>
    );
};

CouponCodeTable.propTypes = {
    couponCodes: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    currentPage: PropTypes.number,
    pageSize: PropTypes.number,
    totalItems: PropTypes.number,
    setCurrentPage: PropTypes.func.isRequired,
    setPageSize: PropTypes.func.isRequired,
    onView: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCodeSearch: PropTypes.func.isRequired,
    searchCode: PropTypes.string,
};

export default CouponCodeTable;
